import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { getMonthStats } from "@/lib/api";

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/50">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "h-9 rounded-none bg-white border-black/15 text-black placeholder:text-black/35 font-mono-plex text-xs focus-visible:ring-1 focus-visible:ring-black/30 focus-visible:border-black/50";

export default function FiltersSidebar({ filters, setFilters, facets, onClear }) {
  const update = (k, v) => setFilters({ ...filters, [k]: v });
  const [months, setMonths] = useState([]);
  const [localQ, setLocalQ] = useState(filters.q || "");

  const handleClear = () => {
    // Reset local search text synchronously so the pending debounce
    // effect cleans up and does not re-apply a stale query.
    setLocalQ("");
    onClear?.();
  };

  useEffect(() => {
    getMonthStats()
      .then((d) => setMonths(d.months || []))
      .catch(() => setMonths([]));
  }, []);

  // Reset local search when parent clears (or otherwise resets) filters
  useEffect(() => {
    setLocalQ(filters.q || "");
  }, [filters.q]);

  // 300ms debounce — avoid firing /api/conversations on every keystroke
  useEffect(() => {
    if (localQ === (filters.q || "")) return;
    const t = setTimeout(() => {
      // Only touch `q` — merge into whatever the latest filters object is at fire time
      setFilters((prev) => (typeof prev === "function" ? prev : { ...prev, q: localQ }));
    }, 300);
    return () => clearTimeout(t);
  }, [localQ]);

  return (
    <aside
      className="border-r border-black/10 bg-white p-5 flex flex-col gap-5 min-h-full"
      data-testid="filters-sidebar"
    >
      <div className="flex items-center justify-between pr-8 md:pr-0">
        <h3 className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-black/75">
          Filters
        </h3>
        <Button
          data-testid="clear-filters-button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 rounded-none text-[10px] uppercase tracking-[0.15em] font-mono-plex text-black/55 hover:text-black hover:bg-black/5"
          onClick={handleClear}
        >
          <X className="w-3 h-3 mr-1" />
          Clear
        </Button>
      </div>

      <Field label="Search transcript">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/40" />
          <Input
            data-testid="filter-search-input"
            className={`${inputCls} pl-8`}
            value={localQ}
            placeholder="grep messages…"
            onChange={(e) => setLocalQ(e.target.value)}
          />
        </div>
      </Field>

      <Field label="Month">
        <select
          data-testid="filter-month"
          className={`${inputCls} px-2 appearance-none`}
          value={filters.month || ""}
          onChange={(e) => update("month", e.target.value)}
        >
          <option value="">any month</option>
          {months.map((m) => (
            <option key={m.month} value={m.month}>
              {m.label} · {m.count.toLocaleString()}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Archive">
        <select
          data-testid="filter-archived"
          className={`${inputCls} px-2 appearance-none`}
          value={filters.archived || ""}
          onChange={(e) => update("archived", e.target.value)}
        >
          <option value="">all records</option>
          <option value="exclude">recent only (not archived)</option>
          <option value="only">archived only</option>
        </select>
      </Field>

      <Field label="Date from">
        <Input
          data-testid="filter-date-from"
          type="date"
          className={inputCls}
          value={filters.date_from}
          onChange={(e) => update("date_from", e.target.value)}
        />
      </Field>
      <Field label="Date to">
        <Input
          data-testid="filter-date-to"
          type="date"
          className={inputCls}
          value={filters.date_to}
          onChange={(e) => update("date_to", e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Min dur (s)">
          <Input
            data-testid="filter-duration-min"
            type="number"
            min="0"
            className={inputCls}
            value={filters.duration_min}
            onChange={(e) => update("duration_min", e.target.value)}
          />
        </Field>
        <Field label="Max dur (s)">
          <Input
            data-testid="filter-duration-max"
            type="number"
            min="0"
            className={inputCls}
            value={filters.duration_max}
            onChange={(e) => update("duration_max", e.target.value)}
          />
        </Field>
      </div>

      <Field label="Status">
        <select
          data-testid="filter-status"
          className={`${inputCls} px-2 appearance-none`}
          value={filters.status}
          onChange={(e) => update("status", e.target.value)}
        >
          <option value="">any</option>
          {(facets.statuses || []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Field>

      <Field label="Call successful">
        <select
          data-testid="filter-call-successful"
          className={`${inputCls} px-2 appearance-none`}
          value={filters.call_successful}
          onChange={(e) => update("call_successful", e.target.value)}
        >
          <option value="">any</option>
          {(facets.call_successful || []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Field>

      <Field label="Environment">
        <select
          data-testid="filter-environment"
          className={`${inputCls} px-2 appearance-none`}
          value={filters.environment}
          onChange={(e) => update("environment", e.target.value)}
        >
          <option value="">any</option>
          {(facets.environments || []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Field>

      <div className="mt-4 pt-4 border-t border-black/10">
        <p className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/40 leading-relaxed">
          Full-text search runs against transcript, title, and agent name using case-insensitive regex. Archived records keep transcript + metadata; raw payloads are trimmed.
        </p>
      </div>
    </aside>
  );
}
